import axios from "axios";

// API Base URL
// Create axios instance with default config
const apiClient = axios.create({
  timeout: 30000, // 30 seconds
  headers: {
    "Content-Type": "application/json",
  },
});

// Use the base URL set in src/index.js
apiClient.interceptors.request.use((config) => {
  config.baseURL = axios.defaults.baseURL;
  return config;
});

// Error handling utility
const handleApiError = (error, context = "API call") => {
  console.error(`Error in ${context}:`, error);
  
  let errorMessage = "An unexpected error occurred";
  let errorCode = "UNKNOWN_ERROR";
  
  if (error.code === 'ECONNABORTED') {
    errorMessage = "Request timeout. Please try again.";
    errorCode = "TIMEOUT";
  } else if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    errorCode = `HTTP_${status}`;
    
    if (data?.message) {
      errorMessage = data.message;
    } else if (status === 401) {
      errorMessage = "Unauthorized access. Please login again.";
    } else if (status === 403) {
      errorMessage = "Access forbidden. Insufficient permissions.";
    } else if (status === 404) {
      errorMessage = "Resource not found.";
    } else if (status === 500) {
      errorMessage = "Internal server error. Please try again later.";
    } else {
      errorMessage = `Request failed with status ${status}`;
    }
  } else if (error.request) {
    // Request made but no response
    errorMessage = "No response received from server. Check network connection.";
    errorCode = "NETWORK_ERROR";
  } else {
    // Other errors
    errorMessage = error.message || errorMessage;
  }
  
  return {
    success: false,
    error: errorMessage,
    code: errorCode,
    details: error.response?.data,
  };
};

// Data transformation utilities
const transformLocationData = (item) => ({
  loc_code: item.locCode || item.LocCode || item.loc_code || item.code || item.Code || "",
  loc_name: item.locName || item.LocName || item.loc_name || item.name || item.Name || "",
  status: item.status || item.Status || "A",
});

const transformEmployeeData = (item, locationCode) => ({
  service_no: item.service_no || item.sno || item.ServiceNo || item.employeeId || "",
  Name: item.Name || item.name || item.employeeName || "",
  Department: item.Department || item.dept || item.department || "",
  Designation: item.Designation || item.designation || item.position || "",
  locationCode: item.locationCode || item.locCode || locationCode,
  status: item.status || item.Status || "A",
});

const transformShiftData = (item) => ({
  shift_code: item.shift_code || item.shiftCode || item.code || "",
  shift_name: item.shift_name || item.shiftName || item.name || "",
  StartTime: item.StartTime || item.startTime || item.start_time || "00:00",
  EndTime: item.EndTime || item.endTime || item.end_time || "00:00",
  status: item.status || "A",
  Duration: item.Duration || item.duration || "",
});

const transformRosterDetails = (item) => ({
  WorkDate: item.wDate || item.WorkDate || item.date || "",
  Name: item.RName || item.Name || item.name || item.employeeName || "",
  service_no: item.sNo || item.service_no || item.sno || item.ServiceNo || "",
  shift_name: item.shiftName || item.shift_name || item.shift || "",
  shift_code: item.shiftCode || item.shift_code || "",
  location: item.location || item.LocName || item.locationName || "",
  // Include original fields for debugging
  original: item,
});

// API Services
const RosterManagementService = {
  // ==================== LOCATIONS ====================
  
  /**
   * Fetch all locations
   * @returns {Promise} - Array of location objects
   */
  fetchLocations: async () => {
    try {
      console.log("Fetching locations from:", "/UserDetails/Locations");
      
      const response = await apiClient.get("/UserDetails/Locations");
      console.log("Locations API response:", response.data);
      
      let locationsData = [];
      
      if (response.data && response.data.resultSet && Array.isArray(response.data.resultSet)) {
        locationsData = response.data.resultSet.map(transformLocationData);
      } else if (Array.isArray(response.data)) {
        locationsData = response.data.map(transformLocationData);
      } else if (response.data && Array.isArray(response.data)) {
        locationsData = response.data.map(transformLocationData);
      }
      
      console.log(`Processed ${locationsData.length} locations`);
      
      return {
        success: true,
        data: locationsData,
        rawResponse: response.data,
      };
    } catch (error) {
      return handleApiError(error, "fetchLocations");
    }
  },
  
  // ==================== EMPLOYEES ====================
  
  /**
   * Fetch employees by location
   * @param {string} locationCode - Location code
   * @returns {Promise} - Array of employee objects
   */
  fetchEmployeesByLocation: async (locationCode) => {
    try {
      if (!locationCode) {
        return {
          success: false,
          error: "Location code is required",
        };
      }
      
      console.log(`Fetching employees for location: ${locationCode}`);
      
      const response = await apiClient.get(
        `/UserDetails/LocEmployee?loc=${encodeURIComponent(locationCode)}`
      );
      
      console.log(`Employees response for ${locationCode}:`, response.data);
      
      let employeesData = [];
      
      if (response.data && response.data.resultSet && Array.isArray(response.data.resultSet)) {
        employeesData = response.data.resultSet.map(item => transformEmployeeData(item, locationCode));
      } else if (Array.isArray(response.data)) {
        employeesData = response.data.map(item => transformEmployeeData(item, locationCode));
      }
      
      console.log(`Processed ${employeesData.length} employees for location ${locationCode}`);
      
      return {
        success: true,
        data: employeesData,
        locationCode,
        rawResponse: response.data,
      };
    } catch (error) {
      return handleApiError(error, `fetchEmployeesByLocation (${locationCode})`);
    }
  },
  
  // ==================== SHIFTS/SCHEDULES ====================
  
  /**
   * Fetch all shifts/schedules
   * @returns {Promise} - Array of shift objects
   */
  fetchShifts: async () => {
    try {
      console.log("Fetching shifts from API...");
      
      const response = await apiClient.get("/Roster/GetSheduledetails");
      console.log("Shifts API response:", response.data);
      
      let shiftsData = [];
      
      if (response.data && response.data.resultSet && Array.isArray(response.data.resultSet)) {
        shiftsData = response.data.resultSet
          .filter(shift => shift.status === "A")
          .map(transformShiftData);
      } else if (Array.isArray(response.data)) {
        shiftsData = response.data
          .filter(shift => shift.status === "A")
          .map(transformShiftData);
      }
      
      console.log(`Processed ${shiftsData.length} active shifts`);
      
      return {
        success: true,
        data: shiftsData,
        rawResponse: response.data,
      };
    } catch (error) {
      return handleApiError(error, "fetchShifts");
    }
  },
  
  /**
   * Add new shift/schedule
   * @param {Object|Array} scheduleData - Shift data to post
   * @returns {Promise} - Success/error response
   */
  addShift: async (scheduleData) => {
    try {
      const formattedData = Array.isArray(scheduleData) ? scheduleData : [scheduleData];
      
      console.log("Posting new shift:", formattedData);
      
      const response = await apiClient.post(
        "/Roster/PostSchedule",
        formattedData
      );
      
      console.log("Add shift response:", response.data);
      
      if (response.data && response.data.statusCode === 200) {
        return {
          success: true,
          data: response.data,
          message: "Shift added successfully!",
        };
      } else {
        return {
          success: false,
          error: response.data?.message || "Failed to add shift",
          rawResponse: response.data,
        };
      }
    } catch (error) {
      return handleApiError(error, "addShift");
    }
  },
  
  // ==================== ROSTER ASSIGNMENTS ====================
  
  /**
   * Assign roster to employees
   * @param {Array} rosterData - Array of roster assignments
   * @returns {Promise} - Success/error response
   */
  assignRoster: async (rosterData) => {
    try {
      if (!Array.isArray(rosterData) || rosterData.length === 0) {
        return {
          success: false,
          error: "Roster data is required and must be an array",
        };
      }
      
      const formattedData = rosterData;
      
      console.log(`Assigning ${formattedData.length} roster entries:`, formattedData);
      
      const response = await apiClient.post(
        "/Roster/PostEmployeeRoster",
        formattedData
      );
      
      console.log("Assign roster response:", response.data);
      
      // Check for success
      if (response.status === 200 || response.data?.statusCode === 200) {
        return {
          success: true,
          data: response.data,
          message: `Roster assigned successfully! (${formattedData.length} entries)`,
          count: formattedData.length,
        };
      } else {
        return {
          success: false,
          error: response.data?.message || "Failed to assign roster",
          rawResponse: response.data,
        };
      }
    } catch (error) {
      return handleApiError(error, "assignRoster");
    }
  },
  
  /**
   * Fetch roster details
   * @returns {Promise} - Array of roster detail objects
   */
  fetchRosterDetails: async () => {
    try {
      console.log("Fetching roster details...");
      
      const response = await apiClient.get("/Roster/GetRosterdetails");
      console.log("Roster details response:", response.data);
      
      let rosterDetails = [];
      
      if (response.data && response.data.resultSet && Array.isArray(response.data.resultSet)) {
        rosterDetails = response.data.resultSet;
      } else if (Array.isArray(response.data)) {
        rosterDetails = response.data;
      }
      
      console.log(`Processed ${rosterDetails.length} roster details`);
      
      return {
        success: true,
        data: rosterDetails,
        rawResponse: response.data,
      };
    } catch (error) {
      return handleApiError(error, "fetchRosterDetails");
    }
  },
  
  /**
   * Fetch all roster details with optional filters
   * @param {Object} filters - Filter parameters
   * @param {string} filters.loc - Location filter
   * @param {string} filters.sno - Employee number filter
   * @param {string} filters.scode - Shift code filter
   * @param {string} filters.wdate - Work date filter (YYYY-MM-DD)
   * @returns {Promise} - Array of filtered roster details
   */
  fetchAllRosterDetails: async (filters = {}) => {
    try {
      const { loc = "", sno = "", scode = "", wdate = "" } = filters;
      
      const params = new URLSearchParams();
      if (loc) params.append("loc", loc);
      if (sno) params.append("sno", sno);
      if (scode) params.append("scode", scode);
      if (wdate) params.append("wdate", wdate);
      
      const queryString = params.toString() ? `?${params.toString()}` : "";
      
      console.log("Fetching all roster details with filters:", filters);
      
      const response = await apiClient.get(`/Roster/GetAllRosterDetails${queryString}`);
      console.log("All roster details API response:", response.data);
      
      let allRosterDetails = [];
      
      if (response.data && response.data.resultSet && Array.isArray(response.data.resultSet)) {
        allRosterDetails = response.data.resultSet.map(transformRosterDetails);
      } else if (Array.isArray(response.data)) {
        allRosterDetails = response.data.map(transformRosterDetails);
      }
      
      console.log(`Processed ${allRosterDetails.length} roster details`);
      
      if (allRosterDetails.length > 0) {
        console.log("Sample roster item:", allRosterDetails[0]);
      }
      
      return {
        success: true,
        data: allRosterDetails,
        filters: filters,
        rawResponse: response.data,
      };
    } catch (error) {
      return handleApiError(error, "fetchAllRosterDetails");
    }
  },
  
  // ==================== BULK OPERATIONS ====================
  
  /**
   * Bulk assign roster with progress tracking
   * @param {Array} rosterData - Array of roster assignments
   * @param {Function} onProgress - Progress callback (percentage)
   * @returns {Promise} - Success/error response
   */
  bulkAssignRoster: async (rosterData, onProgress = null) => {
    try {
      if (!Array.isArray(rosterData) || rosterData.length === 0) {
        return {
          success: false,
          error: "Roster data is required and must be an array",
        };
      }
      
      // For large datasets, split into chunks
      const CHUNK_SIZE = 50; // Process 50 records at a time
      const chunks = [];
      
      for (let i = 0; i < rosterData.length; i += CHUNK_SIZE) {
        chunks.push(rosterData.slice(i, i + CHUNK_SIZE));
      }
      
      const results = {
        total: rosterData.length,
        successful: 0,
        failed: 0,
        errors: [],
      };
      
      // Process each chunk
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        
        try {
          const response = await apiClient.post(
            "/Roster/PostEmployeeRoster",
            chunk
          );
          
          if (response.status === 200 || response.data?.statusCode === 200) {
            results.successful += chunk.length;
          } else {
            results.failed += chunk.length;
            results.errors.push({
              chunk: i + 1,
              error: response.data?.message || "Chunk processing failed",
            });
          }
        } catch (error) {
          results.failed += chunk.length;
          results.errors.push({
            chunk: i + 1,
            error: error.message || "Chunk processing error",
          });
        }
        
        // Report progress
        if (onProgress) {
          const progress = Math.round(((i + 1) / chunks.length) * 100);
          onProgress(progress);
        }
      }
      
      return {
        success: results.failed === 0,
        data: results,
        message: `Bulk assignment completed: ${results.successful} successful, ${results.failed} failed`,
      };
    } catch (error) {
      return handleApiError(error, "bulkAssignRoster");
    }
  },
  
  // ==================== VALIDATION ENDPOINTS ====================
  
  /**
   * Validate roster assignment before posting
   * @param {Array} rosterData - Array of roster assignments to validate
   * @returns {Promise} - Validation results
   */
  validateRosterAssignment: async (rosterData) => {
    try {
      if (!Array.isArray(rosterData) || rosterData.length === 0) {
        return {
          success: false,
          error: "Roster data is required for validation",
        };
      }
      
      // Check for duplicates
      const duplicates = [];
      const seen = new Set();
      
      rosterData.forEach((item, index) => {
        const key = `${item.service_no}-${item.WorkDate}-${item.shift_code}`;
        if (seen.has(key)) {
          duplicates.push({
            index,
            employee: item.service_no,
            date: item.WorkDate,
            shift: item.shift_code,
          });
        } else {
          seen.add(key);
        }
      });
      
      // Validate required fields
      const missingFields = [];
      rosterData.forEach((item, index) => {
        if (!item.service_no) {
          missingFields.push({
            index,
            field: "service_no",
            item,
          });
        }
        if (!item.WorkDate) {
          missingFields.push({
            index,
            field: "WorkDate",
            item,
          });
        }
        if (!item.shift_code) {
          missingFields.push({
            index,
            field: "shift_code",
            item,
          });
        }
      });
      
      return {
        success: true,
        data: {
          totalEntries: rosterData.length,
          uniqueEntries: seen.size,
          duplicates: {
            count: duplicates.length,
            items: duplicates,
          },
          missingFields: {
            count: missingFields.length,
            items: missingFields,
          },
          isValid: duplicates.length === 0 && missingFields.length === 0,
        },
      };
    } catch (error) {
      return handleApiError(error, "validateRosterAssignment");
    }
  },
  
  // ==================== HEALTH CHECK ====================
  
  /**
   * Test API connectivity
   * @returns {Promise} - Connectivity status
   */
  testAPIConnectivity: async () => {
    try {
      const endpoints = [
        { name: "Locations", url: "/UserDetails/Locations" },
        { name: "Shifts", url: "/Roster/GetSheduledetails" },
        { name: "Roster", url: "/Roster/GetAllRosterDetails" },
      ];
      
      const results = [];
      
      for (const endpoint of endpoints) {
        try {
          const startTime = Date.now();
          const response = await apiClient.get(endpoint.url);
          const endTime = Date.now();
          const responseTime = endTime - startTime;
          
          results.push({
            endpoint: endpoint.name,
            status: "OK",
            statusCode: response.status,
            responseTime: `${responseTime}ms`,
            dataSize: Array.isArray(response.data) ? response.data.length : 1,
          });
        } catch (error) {
          results.push({
            endpoint: endpoint.name,
            status: "ERROR",
            statusCode: error.response?.status || 0,
            error: error.message,
          });
        }
      }
      
      const allOk = results.every(r => r.status === "OK");
      
      return {
        success: allOk,
        data: results,
        message: allOk 
          ? "All API endpoints are accessible!" 
          : "Some API endpoints failed",
      };
    } catch (error) {
      return handleApiError(error, "testAPIConnectivity");
    }
  },
  
  // ==================== UTILITY METHODS ====================
  
  /**
   * Format date to YYYY-MM-DD
   * @param {Date|string} date - Date to format
   * @returns {string} - Formatted date string
   */
  formatDate: (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  },
  
  /**
   * Generate roster summary statistics
   * @param {Array} rosterData - Roster data to analyze
   * @returns {Object} - Summary statistics
   */
  generateRosterSummary: (rosterData) => {
    if (!Array.isArray(rosterData) || rosterData.length === 0) {
      return {
        total: 0,
        employees: 0,
        dates: 0,
        shifts: 0,
        locations: 0,
      };
    }
    
    const uniqueEmployees = new Set();
    const uniqueDates = new Set();
    const uniqueShifts = new Set();
    const uniqueLocations = new Set();
    
    rosterData.forEach(item => {
      if (item.service_no) uniqueEmployees.add(item.service_no);
      if (item.WorkDate) uniqueDates.add(item.WorkDate);
      if (item.shift_code) uniqueShifts.add(item.shift_code);
      if (item.location) uniqueLocations.add(item.location);
    });
    
    return {
      total: rosterData.length,
      employees: uniqueEmployees.size,
      dates: uniqueDates.size,
      shifts: uniqueShifts.size,
      locations: uniqueLocations.size,
      uniqueEmployees: Array.from(uniqueEmployees),
      uniqueDates: Array.from(uniqueDates),
      uniqueShifts: Array.from(uniqueShifts),
    };
  },
  
  /**
   * Download roster report as CSV
   * @param {Array} rosterData - Roster data to export
   * @param {string} filename - Output filename
   */
  exportRosterToCSV: (rosterData, filename = "roster_export.csv") => {
    if (!Array.isArray(rosterData) || rosterData.length === 0) {
      console.warn("No data to export");
      return false;
    }
    
    try {
      // Define CSV headers
      const headers = [
        "Work Date",
        "Employee Name",
        "Service No",
        "Shift Name",
        "Shift Code",
        "Start Time",
        "End Time",
        "Location",
        "Status"
      ];
      
      // Create CSV rows
      const rows = rosterData.map(item => [
        item.WorkDate || "",
        item.Name || "",
        item.service_no || "",
        item.shift_name || "",
        item.shift_code || "",
        item.StartTime || "",
        item.EndTime || "",
        item.location || "",
        item.status || "A",
      ]);
      
      // Combine headers and rows
      const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
      ].join("\n");
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error("Error exporting CSV:", error);
      return false;
    }
  },
};

export default RosterManagementService;

// Also export individual functions for selective imports
export const {
  fetchLocations,
  fetchEmployeesByLocation,
  fetchShifts,
  addShift,
  assignRoster,
  fetchRosterDetails,
  fetchAllRosterDetails,
  bulkAssignRoster,
  validateRosterAssignment,
  testAPIConnectivity,
  formatDate,
  generateRosterSummary,
  exportRosterToCSV,
} = RosterManagementService;